import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouterState } from '@tanstack/react-router';
import concat from 'lodash/concat';
import isEmpty from 'lodash/isEmpty';
import { selectIsNavBarCollapsed } from 'features/loggedInRoutesPanel/slice';
import { setIsSectionsExpanded } from 'store/srSlice';
import {
  resetFieldValidationStatus,
  selectForceShowValidationError,
  selectIsNewSrFieldsValid,
  setForceShowError,
} from 'store/fieldValidationsSlice';
import { selectFormData, updateIsDirtyStatus } from 'store/isDirtyFieldSlice';
import { ResourceType, SubResourceType } from 'services/attachments';
import { useReassignTempAttachmentsMutation } from 'remote-state/attachments';
import { useApplicationData } from 'remote-state/applicationHooks';
import { QUERIES_KEYS } from 'constant';
import {
  useCategoryDrivenTemplate,
  useSaveSR,
  useSR,
  useUpdateAssociatedFields,
} from 'remote-state/ticketServiceHooks';
import usePreviousValue from 'common/utils/hooks/usePreviousValue';
import { useCategoryDrivenEnabled } from 'remote-state/accountSettingsServiceHooks';
import Header from 'features/header/header';
import SrPanel from 'features/srPanel';
import { useGetTemplateById } from 'remote-state/templateHooks';
import { mapToSrKey } from 'common/utils/srUtils';
import { castCategoryDefaultToNumber } from 'features/templateBuilder/utils/utils';
import { getRichTextValueByColumnName } from 'common/components/controls/RichTextEditor/utils';
import { fieldValueExists, getFieldsMap } from 'common/utils/fieldUtils';
import { RICH_TEXT_FIELD_IDS } from 'features/srPanel/consts';
import usePerformanceMeasure from 'common/utils/hooks/usePerformanceMeasure';
import { PERFORMANCE_MEASURE } from 'constants/performanceMeasure';
import { APP_CONSTANTS } from 'constants/index';
import { SR_CODE_TYPE_MAP, SR_TYPES } from '../TicketPanel/constants';
import { FloatingTicketInstanceActions, FloatingTicketInstanceModes } from './floatingTicketPanelManager/modes';
import { parseEmptyCategories } from './utils/utils';
import FloatingActions from './actions/floatingActions';
import FloatingHeader from './floatingHeader';
import { StyledFloatingHeaderWrapper, StyledFloatingTicketWrapper } from './style';
import { CONSTANTS } from './utils/constants';

const { NEW_VAL } = CONSTANTS;

export default function FloatingTicketPanel() {
  const isFieldsValid = useSelector(selectIsNewSrFieldsValid);
  const dispatch = useDispatch();
  const routerState = useRouterState();
  const { search, href, pathname } = routerState.location;
  const [isSaving, setIsSaving] = useState(false);
  const { data: isTripleCategory } = useApplicationData(QUERIES_KEYS.IS_EXTERNAL_MODE);
  const { mutateAsync: saveSR } = useSaveSR();
  const { mutateAsync: reassignTempAttachments } = useReassignTempAttachmentsMutation();
  const [drivenTemplate, setCategoryDrivenTemplate] = useState();
  const { sr, template } = useSR();
  const [srObject, setSrObject] = useState(sr?.data);
  const [srFieldsToUpdate, setSrFieldsToUpdate] = useState();
  const [mode, setMode] = useState(FloatingTicketInstanceModes.OPENED);
  const [panelWidth, setPanelWidth] = useState(0);
  const [currentTemplateId, setCurrentTemplateId] = useState(NEW_VAL);
  const isNavBarCollapsed = useSelector(selectIsNavBarCollapsed);
  const [srPanelCollapsed, setSrPanelCollapsed] = useState(false);
  const isCategoryDrivenEnabled = useCategoryDrivenEnabled()?.data?.data;
  const formState = useSelector(selectFormData);
  const forceShowError = useSelector(selectForceShowValidationError);
  const { performanceMeasureEventReport } = usePerformanceMeasure();

  // const isPartialCategories =
  //   srObject.primaryCategory && (!srObject.secondaryCategory || (isTripleCategory && !srObject.thirdLevelCategory));

  const srObjectFinishedUpdating = useRef();
  const waitForSrObjectToFinishUpdating = useRef();

  const handleSetSrObject = (newSr) => {
    srObjectFinishedUpdating.current = false;
    setSrObject(newSr);
  };

  useEffect(() => {
    handleSetSrObject(sr?.data);
  }, [sr?.data]);

  const objectUpdates = useRef(-1);

  const { data: updatedSrWithAssociatedFields } = useUpdateAssociatedFields({ srObject, ...srFieldsToUpdate });

  useEffect(() => {
    if (updatedSrWithAssociatedFields) {
      setSrFieldsToUpdate();
      if (updatedSrWithAssociatedFields.richTextFields) {
        let descriptionValue = updatedSrWithAssociatedFields.richTextFields;
        try {
          descriptionValue = JSON.parse(getRichTextValueByColumnName(descriptionValue, 'description').jsonContent).text;
        } catch {
          descriptionValue = updatedSrWithAssociatedFields.richTextFields;
        }
        setSrObject((prev) => ({ ...prev, ...updatedSrWithAssociatedFields, description: descriptionValue }));
      } else {
        setSrObject((prev) => ({ ...prev, ...updatedSrWithAssociatedFields }));
      }
    }
  }, [updatedSrWithAssociatedFields]);

  const pathnameArr = pathname.split('/');
  const page = pathnameArr[pathnameArr.length - 1];
  const type = search.srType || SR_TYPES.incident;
  const srId = 'new';
  const isDirtyFormSelectorProperty = `${page}-${srId}`;
  let templateObject;
  if (template) {
    templateObject = template.data;
  }
  const hasEditPermissions = true;

  if (currentTemplateId === NEW_VAL && templateObject?.header?.length > 0) {
    setCurrentTemplateId(templateObject.id);
  }
  const loadingNonDefaultTemplate =
    currentTemplateId !== NEW_VAL && templateObject?.id && currentTemplateId !== templateObject?.id;
  const templateData = useGetTemplateById(loadingNonDefaultTemplate ? currentTemplateId : undefined, 'ticket');
  let currentTemplate = loadingNonDefaultTemplate ? templateData?.data : templateObject;
  if (drivenTemplate) {
    currentTemplate = drivenTemplate;
  }

  const fields = useMemo(
    () => currentTemplate?.sections?.flatMap((section) => section?.sectionRows?.flatMap((row) => row.fields)),
    [currentTemplate],
  );

  const fieldsMap = useMemo(() => getFieldsMap(fields), [fields]);

  const updateSrObject = useCallback((updateObj, key) => {
    objectUpdates.current++;
    srObjectFinishedUpdating.current = false;
    setSrFieldsToUpdate({ updateObj, key });
  }, []);

  const fillDefaultValues = useCallback(() => {
    const newSr = {};
    newSr.ticketTemplateId = currentTemplate?.id;
    newSr.type = type;
    newSr.srType = SR_CODE_TYPE_MAP[type];
    const manuallyChangedFields = formState[isDirtyFormSelectorProperty];

    fields?.forEach((field) => {
      const { fieldName, fieldId, defaultValue } = field;
      const isRichTextField = RICH_TEXT_FIELD_IDS.includes(fieldId);
      const manuallyChangeValue = manuallyChangedFields?.[fieldName]?.value;
      let valueToChange;
      if (manuallyChangeValue) {
        valueToChange = typeof manuallyChangeValue === 'object' ? manuallyChangeValue?.value : manuallyChangeValue;
        if (isRichTextField) {
          valueToChange = JSON.stringify({ text: manuallyChangeValue });
        }
      } else if (fieldValueExists({ value: defaultValue, fieldId, fieldName })) {
        valueToChange = defaultValue;
        if (isRichTextField) {
          valueToChange = getRichTextValueByColumnName(defaultValue, fieldName)?.jsonContent;
        }
      } else if (fieldName === 'requestUser.agreement') {
        valueToChange = 1;
      }
      if (isRichTextField) {
        let jsonContent = valueToChange || JSON.stringify({ text: '' });
        if (typeof jsonContent === 'object') {
          jsonContent = JSON.stringify(jsonContent);
        }
        newSr.richTextFields = [
          {
            referencedEntityTableName: 'SERVICE_REQ',
            columnName: fieldName,
            jsonContent,
          },
        ];
      }
      newSr[fieldName] = valueToChange;
    });

    currentTemplate?.header.forEach((field) => {
      const { fieldId, fieldName, defaultValue } = field;
      const { key, isCategoryKey } = mapToSrKey(fieldName);
      if (manuallyChangedFields?.[fieldName]?.value) {
        newSr[key] = manuallyChangedFields?.[fieldName]?.value;
      } else if (fieldValueExists({ value: defaultValue, fieldId, fieldName })) {
        if (isCategoryKey) {
          newSr[key] = castCategoryDefaultToNumber(defaultValue);
        } else {
          newSr[key] = field.defaultValue;
        }
      }
    });
    handleSetSrObject(newSr);
  }, [fields, currentTemplate?.header, currentTemplate?.id, type, formState, isDirtyFormSelectorProperty]);

  const initSrObject = useCallback(() => {
    objectUpdates.current++;
    // dispatch(removeRichFieldBySource({ sourceId }));
    fillDefaultValues();
  }, [fillDefaultValues]);

  if ((templateData.status !== 'success' && loadingNonDefaultTemplate) || template.status !== 'success') {
    window.parent.postMessage({
      url: href,
      action: FloatingTicketInstanceActions.RequestLoader,
      target: 'parent',
    });
  } else {
    window.parent.postMessage({
      url: href,
      action: FloatingTicketInstanceActions.RequestStopLoader,
      target: 'parent',
    });
  }

  useEffect(() => {
    const handleMessage = (e) => {
      //message structure is always an object with this structure: { url: ..., action: FloatingTicketInstanceActions.___, data: value, target: iframe or paren }
      const message = e.data;
      if (message?.target !== 'iframe') return;
      switch (message?.action) {
        case FloatingTicketInstanceActions.RequestModeChange:
          setMode(message.data);
          break;
        case FloatingTicketInstanceActions.CollapsedPanelWidthChange:
          setPanelWidth(message.data);
          break;
        default:
          break;
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleDirtyFields = useCallback(
    (props) => {
      const fieldName = Object.keys(props)[0];
      const value = props[fieldName];

      dispatch(updateIsDirtyStatus({ srId: isDirtyFormSelectorProperty, fieldName, value }));
    },
    [dispatch, isDirtyFormSelectorProperty],
  );

  const handleTemplateChanged = useCallback(
    (template) => {
      setCategoryDrivenTemplate(null);
      dispatch(resetFieldValidationStatus());
      setCurrentTemplateId(template.id);
    },
    [dispatch],
  );

  const scrollToInvalidField = useCallback(() => {
    setTimeout(() => {
      const invalidField = document.querySelector('.with-error');
      if (invalidField) {
        dispatch(setIsSectionsExpanded(true));
        invalidField.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
      }
    }, 150);
  }, [dispatch]);

  const onModeChanged = (mode) => {
    setMode(mode);
  };

  const maybeClosePanel = () => {
    if (!(!isFieldsValid && objectUpdates.current < 1) || (isFieldsValid && objectUpdates.current > 0)) {
      window.parent.postMessage({
        url: href,
        action: FloatingTicketInstanceActions.RequestClosedMessage,
        target: 'parent',
      });
    } else {
      window.parent.postMessage({
        url: href,
        action: FloatingTicketInstanceActions.RequestModeChange,
        data: FloatingTicketInstanceModes.CLOSED,
        target: 'parent',
      });
    }
  };

  const attachmentSubResourceType = (fieldName) => {
    if (fieldName === 'description') return { type: SubResourceType.DESCRIPTION, id: 6 };
  };

  const reassignRtfAttachments = useCallback(
    async (richTextField, srId) => {
      const jsonContent = JSON.parse(richTextField.jsonContent);
      const tempAttachmentsToReassign = concat(jsonContent?.inlineImages || [], jsonContent?.attachments || []).map(
        (file) => file.id,
      );
      if (tempAttachmentsToReassign.length) {
        const subResource = attachmentSubResourceType(richTextField.columnName);
        await reassignTempAttachments({
          attachmentIds: tempAttachmentsToReassign,
          param: {
            targetSubResourceId: subResource.id,
            targetSubResourceType: subResource.type,
            resourceId: srId,
            resourceType: ResourceType.SR,
          },
        });
      }
    },
    [reassignTempAttachments],
  );

  const srBaseFields = useMemo(() => {
    const fields = ['type', 'srType', 'ticketTemplateId', 'richTextFields', 'customColumnsFields'];
    return currentTemplate?.header
      ? [...fields, ...currentTemplate?.header.map((field) => mapToSrKey(field.fieldName).key)]
      : fields;
  }, [currentTemplate?.header]);

  const handleSaveSR = useCallback(async () => {
    if (!isFieldsValid) {
      dispatch(setForceShowError(true));
      scrollToInvalidField();
      return;
    }
    if (!srObjectFinishedUpdating.current) {
      waitForSrObjectToFinishUpdating.current = true;
      return;
    }
    const startSavingTime = performance.now();
    setIsSaving(true);
    let updatedSrObject = { ...srObject };

    delete updatedSrObject.description;
    const customColumnsFields = {};

    Object.keys(updatedSrObject).forEach((srKey) => {
      const field = fieldsMap[srKey];
      if (!field && !srBaseFields.includes(srKey)) {
        // we're removing the fields that doesn't belong to the current template from the sr before save, but we are keeping them in the sr object for consistency of values
        delete updatedSrObject[srKey];
      }
      if (field?.customColumn) {
        customColumnsFields[srKey] = updatedSrObject[srKey] || null;
        delete updatedSrObject[srKey];
      }
    });
    if (!isEmpty(customColumnsFields)) {
      updatedSrObject.customColumnsFields = customColumnsFields;
    }

    // TODO: Remove this function, utils and utils.test files - when BE will support to save categories with undefined values.
    updatedSrObject = parseEmptyCategories(srId, isTripleCategory, updatedSrObject);

    const newAuditLogRes = await saveSR(updatedSrObject, {
      onSuccess: async ({ sr }) => {
        if (!sr) {
          setIsSaving(false);
          window.parent.postMessage({
            url: href,
            action: FloatingTicketInstanceActions.ErrorCreatingTicket,
            target: 'parent',
          });
          return;
        }
        if (updatedSrObject?.richTextFields?.length) {
          const promises = [];
          for (const richTextField of updatedSrObject.richTextFields) {
            promises.push(reassignRtfAttachments(richTextField, sr.id));
          }
          if (promises.length) await Promise.all(promises);
        }
        setIsSaving(false);
        if (sr) {
          const endSavingTime = performance.now();
          const duration = endSavingTime - startSavingTime;
          performanceMeasureEventReport(PERFORMANCE_MEASURE.EVENTS.CREATED_TICKET_ADDED_TO_QUEUE, {
            tenantId: localStorage.getItem(APP_CONSTANTS.ACCOUNT_ID_LOCAL_KEY),
            duration,
          });
          window.parent.postMessage({
            url: href,
            action: FloatingTicketInstanceActions.TicketCreated,
            data: sr,
            target: 'parent',
          });
        }
      },
      onError: (error) => {
        setIsSaving(false);
        console.log(error);
        window.parent.postMessage({
          url: href,
          action: FloatingTicketInstanceActions.ErrorCreatingTicket,
          target: 'parent',
        });
      },
    });

    return newAuditLogRes;
  }, [
    isFieldsValid,
    isTripleCategory,
    srObject,
    fieldsMap,
    srBaseFields,
    saveSR,
    dispatch,
    scrollToInvalidField,
    reassignRtfAttachments,
    href,
    performanceMeasureEventReport,
  ]);

  const prevSrObject = usePreviousValue(srObject);

  useEffect(() => {
    if (srObject && prevSrObject !== srObject) {
      srObjectFinishedUpdating.current = true;
      if (waitForSrObjectToFinishUpdating.current) {
        waitForSrObjectToFinishUpdating.current = false;
        handleSaveSR();
      }
    }
  }, [prevSrObject, srObject, handleSaveSR]);

  const prevCurrentTemplate = usePreviousValue(currentTemplate);

  useEffect(() => {
    if (prevCurrentTemplate !== currentTemplate && currentTemplate && templateObject) {
      initSrObject();
    }
  }, [prevCurrentTemplate, currentTemplate, initSrObject, templateObject]);

  const categoryDrivenTemplate = useCategoryDrivenTemplate(type);
  if (categoryDrivenTemplate.data?.header?.length > 0 && categoryDrivenTemplate.data?.id !== currentTemplateId) {
    const templateData = categoryDrivenTemplate?.data;
    handleTemplateChanged(templateData);
    setCategoryDrivenTemplate(templateData);
  }

  const toggleSrPanelCollapse = () => {
    setSrPanelCollapsed(!srPanelCollapsed);
  };

  if (!srObject.ticketTemplateId) {
    return null;
  }
  return (
    <StyledFloatingTicketWrapper data-testid="new-ticket-panel">
      <StyledFloatingHeaderWrapper>
        <FloatingHeader
          isCategoryDrivenTemplatesEnabled={isCategoryDrivenEnabled}
          ticketTitle={srObject?.title}
          mode={mode}
          onModeChanged={onModeChanged}
          srType={type}
          onTemplateChanged={handleTemplateChanged}
          currentTemplate={currentTemplate}
          onCloseClicked={maybeClosePanel}
          panelWidth={panelWidth}
        />
        <Header
          isHorizontalView={false}
          srObject={srObject}
          handleSaveSR={updateSrObject}
          template={currentTemplate}
          isNewSR
          handleDirty={handleDirtyFields}
          isFloatingTicketPanel
          isValidTicket="true"
          hasEditPermissions={hasEditPermissions}
          className="floating-ticket"
        />
      </StyledFloatingHeaderWrapper>

      <div style={{ position: 'relative' }}>
        <SrPanel
          sr={srObject}
          isNavBarCollapsed={isNavBarCollapsed}
          srPanelCollapsed={srPanelCollapsed}
          toggleSrPanelCollapse={toggleSrPanelCollapse}
          handleSaveSR={updateSrObject}
          template={currentTemplate}
          isFloatingTicketPanel
          hasEditPermissions={hasEditPermissions}
          handleDirtyFields={handleDirtyFields}
        />
      </div>

      <FloatingActions
        isNotValid={!isFieldsValid && forceShowError}
        // isNotValid={(!isFieldsValid || !isPartialCategories) && forceShowError}
        isSaving={isSaving}
        onSaveClicked={handleSaveSR}
        onCancelClicked={maybeClosePanel}
      />
    </StyledFloatingTicketWrapper>
  );
}
